import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { backendEndPointToken } from '../../app.config';
import { StateService } from '../state.service';
import { Bundle, ChargeItem, Invoice, ServiceRequest } from 'fhir/r4';
import { catchError, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { referenceArrayContains, referenceMatches } from '../fhir-reference.util';

/**
 * NOTE: This service is intentionally a COPY of the lab billing placeholder patterns,
 * adapted for general ServiceRequest categories. We keep it separate so lab flows
 * remain stable and unchanged.
 */
@Injectable({
  providedIn: 'root'
})
export class ServiceRequestBillingPlaceholderService {
  private readonly http = inject(HttpClient);
  private readonly backendEndPoint = inject(backendEndPointToken);
  private readonly stateService = inject(StateService);

  /**
   * Generic payment resolver:
   * - Find ChargeItem(s) whose `service[]` contains `ServiceRequest/{id}`
   * - Confirm at least one BALANCED Invoice references one of those ChargeItems (lineItem.chargeItemReference)
   */
  resolvePaymentForServiceRequest(
    serviceRequest: ServiceRequest,
    patientId: string
  ): Observable<{ chargeItems: ChargeItem[]; invoices: Invoice[]; hasBalancedInvoice: boolean }> {
    const srId = (serviceRequest?.id ?? '').toString();
    if (!srId) return of({ chargeItems: [], invoices: [], hasBalancedInvoice: false });
    const srRef = `ServiceRequest/${srId}`;

    const processBundle = (bundle: Bundle<any>) => {
      const currentPatientId = this.stateService.currentPatientId$.getValue();
      const isCurrentPatient = !!currentPatientId && currentPatientId === patientId;
      if (isCurrentPatient) {
        this.stateService.processBundleTransaction(bundle as any);
      } else {
        this.stateService.processOrgWideBundleTransaction(bundle as any);
      }
    };

    const fromStateChargeItems = (this.stateService.PatientResources.chargeItems.getValue() || []).map((w) => w.actualResource);
    const fromStateInvoices = (this.stateService.PatientResources.invoices.getValue() || []).map((w) => w.actualResource);

    const chargeItems$ = fromStateChargeItems.length
      ? of(fromStateChargeItems)
      : this.http
        .get<Bundle<ChargeItem>>(`${this.backendEndPoint}/ChargeItem?subject=${encodeURIComponent(`Patient/${patientId}`)}&_count=1000`)
        .pipe(
          tap((bundle) => processBundle(bundle as any)),
          map((b: any) => (b?.entry || []).map((e: any) => e?.resource).filter(Boolean) as ChargeItem[]),
          catchError(() => of([] as ChargeItem[]))
        );

    // We intentionally fetch invoices broadly (mirrors lab service) and filter locally.
    const invoices$ = fromStateInvoices.length
      ? of(fromStateInvoices)
      : this.http
        .get<Bundle<Invoice>>(`${this.backendEndPoint}/Invoice`)
        .pipe(
          tap((bundle) => processBundle(bundle as any)),
          map((b: any) => (b?.entry || []).map((e: any) => e?.resource).filter(Boolean) as Invoice[]),
          catchError(() => of([] as Invoice[]))
        );

    return forkJoin({ chargeItems: chargeItems$, invoices: invoices$ }).pipe(
      map(({ chargeItems, invoices }) => {
        const linkedChargeItems = (chargeItems || []).filter((ci: any) => {
          const services = Array.isArray(ci?.service) ? ci.service : [];
          return services.some((s: any) => {
            const ref = (s?.reference ?? '').toString();
            if (!ref) return false;
            return ref === srRef || referenceMatches(ref, srRef);
          });
        }) as ChargeItem[];

        const chargeItemRefs = new Set(linkedChargeItems.filter((c: any) => !!c?.id).map((c: any) => `ChargeItem/${c.id}`));

        const hasBalancedInvoice = (invoices || []).some((inv: any) => {
          const status = (inv?.status || '').toString().toLowerCase();
          if (status !== 'balanced') return false;
          const lineItems = inv?.lineItem || [];
          return lineItems.some((li: any) => {
            const ref = (li?.chargeItemReference?.reference || '').toString();
            if (!ref) return false;
            // exact or suffix match
            for (const target of chargeItemRefs) {
              if (ref === target || ref.endsWith(target)) return true;
            }
            return false;
          });
        });

        // Return filtered invoices too (optional, for UI)
        const filteredInvoices = (invoices || []).filter((inv: any) => {
          const lineItems = inv?.lineItem || [];
          return lineItems.some((li: any) => {
            const ref = (li?.chargeItemReference?.reference || '').toString();
            if (!ref) return false;
            for (const target of chargeItemRefs) {
              if (ref === target || ref.endsWith(target)) return true;
            }
            return false;
          });
        }) as Invoice[];

        return { chargeItems: linkedChargeItems, invoices: filteredInvoices, hasBalancedInvoice };
      })
    );
  }

  resolvePaymentForServiceRequests(
    serviceRequests: ServiceRequest[],
    patientId: string
  ): Observable<{ hasBalancedInvoiceForAll: boolean; byId: Record<string, boolean> }> {
    const list = (serviceRequests || []).filter(Boolean);
    if (!list.length) return of({ hasBalancedInvoiceForAll: true, byId: {} });

    return forkJoin(
      list.map((sr) =>
        this.resolvePaymentForServiceRequest(sr, patientId).pipe(
          map((r) => ({ id: (sr?.id ?? '').toString(), ok: r.hasBalancedInvoice }))
        )
      )
    ).pipe(
      map((results) => {
        const byId: Record<string, boolean> = {};
        for (const r of results) {
          if (!r.id) continue;
          byId[r.id] = r.ok;
        }
        return { hasBalancedInvoiceForAll: results.every((r) => r.ok), byId };
      })
    );
  }

  batchResolvePaymentForServiceRequests(
    serviceRequests: ServiceRequest[]
  ): Observable<Record<string, { chargeItems: ChargeItem[]; invoices: Invoice[]; hasBalancedInvoice: boolean }>> {
    const list = (serviceRequests || []).filter((sr) => !!sr.id);
    if (!list.length) return of({});

    const patientIds = new Set<string>();
    for (const sr of list) {
        const patientRef = sr.subject?.reference || '';
        const patientId = patientRef.split('/').pop() || '';
        if (patientId) {
            patientIds.add(patientId);
            patientIds.add(`${this.backendEndPoint}/Patient/${patientId}`);
        }
    }
    const patientIdList = Array.from(patientIds);

    const processBundle = (bundle: Bundle<any>) => {
      this.stateService.processOrgWideBundleTransaction(bundle as any);
    };

    const chargeItems$ = this.http
        .get<Bundle<ChargeItem>>(`${this.backendEndPoint}/ChargeItem?subject=${encodeURIComponent(patientIdList.join(','))}&_count=1000`)
        .pipe(
          tap((bundle) => processBundle(bundle as any)),
          map((b: any) => (b?.entry || []).map((e: any) => e?.resource).filter(Boolean) as ChargeItem[]),
          catchError(() => of([] as ChargeItem[]))
        );

    const invoices$ = this.http
        .get<Bundle<Invoice>>(`${this.backendEndPoint}/Invoice?status=balanced&subject=${encodeURIComponent(patientIdList.join(','))}`)
        .pipe(
          tap((bundle) => processBundle(bundle as any)),
          map((b: any) => (b?.entry || []).map((e: any) => e?.resource).filter(Boolean) as Invoice[]),
          catchError(() => of([] as Invoice[]))
        );

    return forkJoin({ chargeItems: chargeItems$, invoices: invoices$ }).pipe(
      map(({ chargeItems, invoices }) => {
        const allChargeItems = chargeItems || [];
        const allInvoices = invoices || [];

        const result: Record<string, { chargeItems: ChargeItem[]; invoices: Invoice[]; hasBalancedInvoice: boolean }> = {};

        for (const sr of list) {
          const srRef = `ServiceRequest/${sr.id}`;

          const myChargeItems = allChargeItems.filter((ci) => {
            const services = Array.isArray(ci?.service) ? ci.service : [];
            return services.some((s: any) => {
              const ref = (s?.reference ?? '').toString();
              return ref === srRef || referenceMatches(ref, srRef);
            });
          });

          const chargeItemRefs = new Set(myChargeItems.filter((c: any) => !!c?.id).map((c: any) => `ChargeItem/${c.id}`));

          const myInvoices = allInvoices.filter((inv: any) => {
            const lineItems = inv?.lineItem || [];
            return lineItems.some((li: any) => {
              const ref = (li?.chargeItemReference?.reference || '').toString();
              if (!ref) return false;
              for (const target of chargeItemRefs) {
                if (ref === target || ref.endsWith(target)) return true;
              }
              return false;
            });
          }) as Invoice[];

          const hasBalancedInvoice = myInvoices.some((inv) => (inv?.status || '').toString().toLowerCase() === 'balanced');

          result[sr.id!] = {
            chargeItems: myChargeItems,
            invoices: myInvoices,
            hasBalancedInvoice
          };
        }

        return result;
      })
    );
  }
}
